export const PARAMETER_UNITS = [
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percentage' },
  { value: 'currency', label: 'Currency' },
  { value: 'per_thousand', label: 'Rate per $1,000' },
];

export function formatParameterValue(value, unit) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  if (unit === 'percent') return `${shiftDecimal(raw, 2)}%`;
  if (unit === 'currency') return moneyLabel(raw);
  if (unit === 'per_thousand') return `${raw} per $1,000`;
  return raw;
}

export function parameterPrimary(row) {
  const unit = row?.unit || row?.display || '';
  if (unit === 'percent' || unit === 'currency' || unit === 'per_thousand') {
    return formatParameterValue(row.value ?? row.default_value ?? row.defaultValue, unit);
  }
  return row?.display || (row?.value == null ? '' : String(row.value));
}

export function runStatusLabel(status, nothingToPost) {
  if (status === 'posted') return 'Posted';
  if (status === 'charges_created') return 'Project Charges Created';
  if (status === 'reversed') return 'Reversed';
  if (status === 'ready') return nothingToPost ? 'Nothing to post' : 'Ready to Post';
  if (status === 'invalid') return 'Invalid';
  return 'Not Calculated';
}

export function listStatus(row) {
  if (!row.enabled) return 'Disabled';
  if (row.version_status === 'draft') return 'Draft';
  if (row.period_run_status === 'posted') return 'Posted';
  if (row.period_run_status === 'charges_created') return 'Project Charges Created';
  if (row.period_run_status === 'reversed') return 'Reversed';
  if (row.period_run_status === 'invalid') return 'Invalid';
  if (row.period_run_status === 'ready' && row.journal_only && !(row.run_inputs || []).length) {
    return row.period_nothing_to_post ? 'Nothing to post' : 'Posts with project journals';
  }
  if (row.period_run_status === 'ready') return row.period_nothing_to_post ? 'Nothing to post' : 'Ready to Post';
  const missingParameter = (row.parameters || []).some((item) => {
    const raw = (row.parameter_values || {})[item.key];
    const missing = raw == null || raw === '';
    return item.required !== false && missing && (item.default_value == null || item.default_value === '');
  });
  if (missingParameter) return 'Needs Setup';
  if (row.requires_scope) {
    const ready = (row.slices || []).some((slice) => (
      (slice.pairs || []).some((pair) => pair.cost_code_vuid && pair.cost_type_vuid)
    ));
    if (!ready) return 'Needs Setup';
  }
  const missingChoice = (row.job_cost_lines || []).some((line) => !(row.job_cost_choices || {})[line.line_key]);
  if (missingChoice) return 'Needs Setup';
  if ((row.run_inputs || []).length) return 'Needs Input';
  if (row.journal_only) return 'Posts with project journals';
  return 'Not Calculated';
}

export function explainCalculationError(text) {
  const message = String(text || '');
  if (message.startsWith('Missing account mapping:')) {
    const name = message.replace('Missing account mapping:', '').replace(/\.$/, '').trim();
    return `${name} has not been configured.`;
  }
  if (message.includes('was not supplied')) {
    const name = message.replace('Run-entered input "', '').replace('" was not supplied.', '');
    return `Enter ${name} to calculate this period.`;
  }
  if (message === 'No accounting distribution is configured.') {
    return 'This calculation version does not have an accounting method yet. Publish a version that describes the journal before posting.';
  }
  const required = message.match(/^Parameter "(.+)" is required\.$/);
  if (required) return `${required[1]} needs a value.`;
  if (message === 'The proposed journal does not balance.') return 'The accounting entry is out of balance.';
  if (message === 'Destination cost type is required to resolve the expense account.') {
    return 'Choose where this project cost should be posted.';
  }
  if (message.includes('needs a calculated value')) return 'Choose the amount for each side of the accounting entry.';
  if (message.includes('needs an account')) return 'Choose the account for each side of the accounting entry.';
  return message
    .replaceAll('destination_cost_type_vuid', 'destination cost type')
    .replaceAll('destination_cost_code_vuid', 'destination cost code')
    .replaceAll('amount_key', 'amount')
    .replaceAll('role_key', 'account')
    .replaceAll('parameter', 'rate')
    .replaceAll('Parameter', 'Rate');
}

export function accountStrategyLabel(strategy) {
  if (strategy === 'cost_type_expense') return 'Project destination expense account';
  if (strategy === 'configured_account') return 'Specified GL Account';
  if (strategy === 'insurance_recovery') return 'Insurance Recovery';
  if (strategy === 'accrued_insurance') return 'Accrued Insurance';
  return 'Account';
}

export function formulaExpression(formula, labelFor) {
  if (!formula || typeof formula !== 'object') return '';
  if (Object.prototype.hasOwnProperty.call(formula, 'const')) return String(formula.const);
  if (formula.ref) return labelFor(formula.ref, formula.key) || formula.key || '';
  const symbols = { add: '+', sub: '−', mul: '×', div: '÷' };
  if (formula.op && Array.isArray(formula.args)) {
    return formula.args.map((arg) => formulaExpression(arg, labelFor)).join(` ${symbols[formula.op] || formula.op} `);
  }
  return '';
}

export function projectCostPolicy(line) {
  if (line.job_cost_policy === 'always' || line.job_cost_policy === 'never' || line.job_cost_policy === 'project_choice') {
    return line.job_cost_policy;
  }
  return line.creates_project_cost ? 'always' : 'never';
}

export function projectCostPhrase(line) {
  if (line.project_cost_label) return line.project_cost_label;
  const policy = projectCostPolicy(line);
  if (policy === 'always') return 'Create Project Cost';
  if (policy === 'project_choice') return 'Let each project decide';
  return 'Journal Entry Only';
}

export function distributionSummary(distribution, namedValues) {
  const labels = {};
  (namedValues || []).forEach((item) => {
    labels[item.key] = item.label || item.key;
  });
  return ((distribution && distribution.lines) || []).map((line) => {
    const account = line.account || {};
    const strategy = account.strategy === 'named_resolver' ? account.resolver : account.strategy;
    return {
      side: line.side === 'credit' ? 'Credit' : 'Debit',
      amount: labels[line.amount_key] || line.amount_key || '',
      account: account.role_label || accountStrategyLabel(strategy),
      creates_project_cost: projectCostPolicy(line) === 'always',
      project_cost_label: projectCostPhrase(line),
    };
  });
}

function shiftDecimal(raw, places) {
  const text = String(raw).trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;
  const negative = text.startsWith('-');
  const unsigned = negative ? text.slice(1) : text;
  const [whole, frac = ''] = unsigned.split('.');
  const digits = `${whole}${frac}`.padEnd(whole.length + places, '0');
  const point = whole.length + places;
  let left = digits.slice(0, point).replace(/^0+(?=\d)/, '');
  if (!left) left = '0';
  const right = digits.slice(point).replace(/0+$/, '');
  return `${negative ? '-' : ''}${left}${right ? `.${right}` : ''}`;
}

function moneyLabel(raw) {
  if (!/^-?\d+(\.\d+)?$/.test(String(raw).trim())) return String(raw);
  const negative = String(raw).trim().startsWith('-');
  const shifted = shiftDecimal(String(raw).replace('-', ''), 0);
  const [whole, frac = ''] = shifted.split('.');
  const cents = `${frac}00`.slice(0, 2);
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}$${withCommas}.${cents}`;
}
